import React, { useRef, useState } from "react";
import DashboardTopBarAdmin from "../../components/admin/DashboardTopBarAdmin";
import PrimaryButton from "../../components/shared/PrimaryButton";
import ErrorButton from "../../components/shared/ErrorButton";
import Modal from "../../components/shared/Modal";

import menusBlueIcon from "../../assets/admin/icons/menus-blue.svg";
import plusIcon from "../../assets/admin/icons/plus-white.svg";
import MenuFormAdmin from "../../components/admin/menus/MenuFormAdmin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import MenuCardAdmin from "../../components/admin/menus/MenuCardAdmin";

import { fetchMenus, deleteMenu } from "../../util/admin/httpAdmin";
import LoadingIndicatorButton from "../../components/shared/LoadingIndicatorButton";
import './MenusAdmin.css';

function MenusAdmin() {
   const modalRef = useRef();
   const deleteModalRef = useRef();
   const [selectedMenu, setSelectedMenu] = useState(null);
   const [menuToDelete, setMenuToDelete] = useState(null);
   const [searchTerm, setSearchTerm] = useState('');

   const queryClient = useQueryClient();

   const openModal = () => modalRef.current.open();
   const closeModal = () => {
      modalRef.current.close();
      setTimeout(() => {
         setSelectedMenu(null);
      }, 500)
   };

   const openDeleteModal = (menu) => {
      setMenuToDelete(menu);
      deleteModalRef.current.open();
   };

   const closeDeleteModal = () => {
      deleteModalRef.current.close();
      setMenuToDelete(null);
   };

   const handleEditButtonClick = (menu) => {
      setSelectedMenu(menu);
      openModal();
   };

   const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
   };

   const {
      data: menus,
      isPending,
      isError,
      error,
   } = useQuery({
      queryKey: ["adminMenus"],
      queryFn: ({ signal }) => fetchMenus({ signal }),
   });

   const deleteMutation = useMutation({
      mutationFn: deleteMenu,
      onSuccess: () => {
         queryClient.invalidateQueries(["adminMenus"]);
         queryClient.refetchQueries(["adminMenus"]);
         closeDeleteModal();
      },
      onError: (error) => {
         console.error("Fout bij het verwijderen van menu:", error);
      },
   });

   // Filter menus based on search term
   const filteredMenus = menus?.filter(menu => 
      menu.name.toLowerCase().includes(searchTerm.toLowerCase())
   );

   return (
      <div>
         <DashboardTopBarAdmin
            title="Menu's"
            icon={menusBlueIcon}
            description="Hier kan je al jouw menu's bekijken en bewerken"
         >
            <PrimaryButton
               onClick={() => {
                  setSelectedMenu(null);
                  openModal();
               }}
            >
               <div className="g-15-1fr">
                  <img src={plusIcon} alt="add" /> <span>Menu Toevoegen</span>
               </div>
            </PrimaryButton>
         </DashboardTopBarAdmin>

         <div className="py-20 px-20">
            <div className="menus-header">
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Zoek menu's..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            
            {isPending && (
               <div className="flex-1">
                  <span>Menu's laden </span>
                  <LoadingIndicatorButton dark></LoadingIndicatorButton>
               </div>
            )}
            {isError && (
               <p className="c-r">
                  {error.info?.message ||
                     "Er is iets foutgegaan bij het ophalen van de menu's"}
               </p>
            )}

            {menus && menus.length === 0 && (
               <p className="c-r">Je hebt nog geen menus aangemaakt</p>
            )}
            {filteredMenus && filteredMenus.length > 0 && !isError && (
               <div className="menus-grid">
                  {filteredMenus.map((menu) => {
                     return (
                        <MenuCardAdmin
                           key={menu._id}
                           menu={menu}
                           onEditClick={handleEditButtonClick}
                           onDeleteClick={() => openDeleteModal(menu)}
                        />
                     );
                  })}
               </div>
            )}
         </div>

         {/* Modal voor bewerken/toevoegen */}
         <Modal ref={modalRef}>
            <MenuFormAdmin
               key={selectedMenu ? selectedMenu._id : "new"}
               onSuccessClose={closeModal}
               editMode={selectedMenu != null}
               previousData={selectedMenu || {}}
            />
         </Modal>

         {/* Modal voor verwijderen */}
         <Modal ref={deleteModalRef}>
            <div className="py-20 px-20 g-20">
               <h2>Bevestig Verwijderen</h2>
               <p>
                  Weet je zeker dat je het menu "{menuToDelete?.name}" wilt
                  verwijderen?
               </p>
               {deleteMutation.isError && (
                  <p className="c-r">
                     {deleteMutation.error.info?.message ||
                        "Er is iets foutgegaan bij het verwijderen van het menu"}
                  </p>
               )}
               <div className="flex-2">
                  <ErrorButton
                     onClick={() => deleteMutation.mutate(menuToDelete._id)}
                     disabled={deleteMutation.isLoading}
                  >
                     <span className="flex-0-5">
                        <span>Verwijderen</span>
                        {deleteMutation.isLoading && (
                           <LoadingIndicatorButton></LoadingIndicatorButton>
                        )}
                     </span>
                  </ErrorButton>
                  <PrimaryButton
                     className="secondary-button"
                     onClick={closeDeleteModal}
                  >
                     Annuleren
                  </PrimaryButton>
               </div>
            </div>
         </Modal>
      </div>
   );
}

export default MenusAdmin;
